import React, { useState, useEffect } from 'react'
import PutawaySearchBar from './PutawaySearchBar.jsx'
import PutawayResults from './PutawayResults.jsx'
import ItemConfirmModal from './ItemConfirmModal.jsx'
import PutawayScannerModal from './PutawayScannerModal.jsx'
import { releaseCameraStream } from './ScannerInput.jsx'
import { useToast } from '../../hooks/useToast.jsx'
import { api } from '../../utils/api'
import { useFeatureOff } from '../../hooks/useEnvironment'

// Must mirror PutawaySearchBar's default chip selection.
const INITIAL_STATUSES = ['draft', 'in_stock']

// Browse view = recently-registered arrivals still needing a shelf. Older
// no-location stock lives in the "No Location" report, so a re-synced legacy
// item can't resurface at the top of this queue. Tunable window in days.
const PUTAWAY_WINDOW_DAYS = 14

function Putaway() {
  const { toast } = useToast()

  // Off on staging (shared/staging-features.js): search stays, the shelve step does not open.
  const putawayOff = useFeatureOff('putaway')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState(null)

  const [confirmItem, setConfirmItem] = useState(null)
  const [scannerItem, setScannerItem] = useState(null)

  // True while the current list is the browse view (no search term), where the
  // created_within_days window is applied. Drives the "last N days" notice so
  // operators know older items are hidden here (search / No Location has them).
  const [windowed, setWindowed] = useState(false)

  async function onSearch({ q, statuses }) {
    setLoading(true); setError(null); setConfirmItem(null); setScannerItem(null)
    setWindowed(!q)
    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (statuses.length > 0) params.set('status', statuses.join(','))
    // The putaway queue is items still needing a home. When browsing by status
    // only (no search term), ask the backend for unplaced items so already-
    // shelved items don't clutter the list, and scope to recent arrivals so a
    // re-synced legacy item can't resurface (older gaps live in No Location).
    // An explicit search omits both, so a located/old item can still be found.
    if (!q) {
      params.set('has_location', 'false')
      params.set('created_within_days', String(PUTAWAY_WINDOW_DAYS))
    }
    try {
      const res = await api.get(`/api/inventory-locations/items?${params.toString()}`)
      // Python /api/v1/inventory-items returns a bare array; tolerate { items: [] } too
      const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : [])
      setItems(list)
      setHasSearched(true)
    } catch (e) {
      setError(e.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  // Mount-time auto-search: land the operator on a populated list rather
  // than an empty pane. Defaults must match PutawaySearchBar's initial
  // chip state.
  useEffect(() => {
    onSearch({ q: '', statuses: INITIAL_STATUSES })
  }, [])

  // ScannerInput caches the camera MediaStream at module scope so iOS
  // Safari doesn't re-prompt for permission on every scan. Release it
  // when the operator leaves the Putaway page so the camera light /
  // indicator actually turns off.
  useEffect(() => {
    return () => releaseCameraStream()
  }, [])

  function onCommitSuccess({ shelf_id, position }) {
    const verb = scannerItem.current_shelf_id ? 'Moved to' : 'Put away to'
    // Toast shows bay only — warehouse team asked to hide the position.
    toast.success(`${verb} ${shelf_id} ✓`)
    // The browse list is the work still to do, so a shelved item leaves it
    // (Trac #1408). A search can find shelved items on purpose, so there the
    // row stays and shows its new shelf.
    setItems(prev => windowed
      ? prev.filter(it => it.item_id !== scannerItem.item_id)
      : prev.map(it =>
        it.item_id === scannerItem.item_id
          ? { ...it, current_shelf_id: shelf_id, current_position: position ?? null }
          : it
      ))
    setScannerItem(null)
    setConfirmItem(null)
  }

  return (
    <div>
      {putawayOff && (
        <p><span className="badge badge-neutral">Off on staging: {putawayOff}. You can search, but not shelve.</span></p>
      )}
      <PutawaySearchBar onSubmit={onSearch} loading={loading} />

      {hasSearched && windowed && !loading && !error && (
        <div
          className="card"
          style={{
            padding: 'var(--space-3)', marginBottom: 'var(--space-3)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
          }}
        >
          <span className="badge badge-info">Last {PUTAWAY_WINDOW_DAYS} days</span>
          <span className="text-sm text-muted">
            Showing recent arrivals still needing a shelf. Search by title or ID to
            include older items, or open the No Location tab for the full backlog.
          </span>
        </div>
      )}

      <PutawayResults
        items={items}
        hasSearched={hasSearched}
        loading={loading}
        error={error}
        onRowClick={putawayOff ? () => {} : setConfirmItem}
      />

      {confirmItem && !scannerItem && (
        <ItemConfirmModal
          item={confirmItem}
          onConfirm={() => { setScannerItem(confirmItem); setConfirmItem(null) }}
          onCancel={() => setConfirmItem(null)}
        />
      )}

      {scannerItem && (
        <PutawayScannerModal
          item={scannerItem}
          onSuccess={onCommitSuccess}
          onCancel={() => setScannerItem(null)}
        />
      )}

    </div>
  )
}

export default Putaway
